import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { setReservedMediaProjectScope } from '../components/canvas-v2/nodes/shared/reservedMediaBox'

const ProjectIdContext = createContext<string | undefined>(undefined)

/**
 * Set a cookie so the GCP load balancer (HTTP_COOKIE session affinity) routes
 * all requests — including WebSocket upgrades — for the same project to the
 * same backend instance. This keeps the in-memory YDoc room co-located with
 * the REST/chat handlers for that project.
 */
function setAffinityCookie(projectId: string | undefined) {
  if (projectId) {
    document.cookie = `DREAMWRIGHT_PROJECT=${projectId}; path=/; SameSite=Lax`
  }
}

export function ProjectIdProvider({ value, children }: { value: string | undefined; children?: ReactNode }) {
  // Point the upload-media decode cache at this project during render (not in
  // an effect) so no child node or group layout reads another project's cached
  // sizes first. Idempotent: a no-op unless the project actually changed.
  setReservedMediaProjectScope(value)

  useEffect(() => {
    setAffinityCookie(value)
  }, [value])

  return <ProjectIdContext.Provider value={value}>{children}</ProjectIdContext.Provider>
}

export function useOptionalProjectId(): string | undefined {
  return useContext(ProjectIdContext)
}

export function useProjectId(): string {
  const id = useOptionalProjectId()
  if (!id) throw new Error('useProjectId() must be used within a <ProjectIdProvider>')
  return id
}
